import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import {
  getApplicableOrderCondition,
  getEffectiveRequestedAt,
  getItemDataOrAdditionPrice,
  getLocalizedName,
  mergeOrderConditions,
} from '../../../../utility/util/util.service';
import { connect } from 'react-redux';
import { setIsSelectAdditionsModalOpen } from '../../../../redux/modal/modal.actions';
import classnames from 'classnames';
import QtySelector from '../../qty-selector/qty-selector.component';
import { customMessageNotification } from '../../../../utility/notification/notification.service';
import { Alert } from 'reactstrap';
import * as Icon from 'react-feather';
import Marquee from '../../helpers/marquee.component';
import AllergenChips, {
  getAdditionAllergens,
} from '../../helpers/allergen-chips.component';

const AdditionGroup = ({
  index,
  checkout,
  additionGroup,
  foodAllergens,
  foodItemAdditions,

  setIsGroupSatisfied,
  setSelectedOrderItemAdditions,
  selectedOrderItemAdditions,
}) => {
  const intl = useIntl();
  const [error, setError] = useState(null);

  // Additions show the same allergen chips as items, so a guest sees what an
  // extra contains. The additions embedded in the group payload are trimmed and
  // carry no allergens, so they are looked up on the full `foodItemAdditions`.
  const allergensById = useMemo(
    () => new Map((foodAllergens || []).map((allergen) => [allergen.id, allergen])),
    [foodAllergens],
  );

  const additionsById = useMemo(
    () => new Map((foodItemAdditions || []).map((addition) => [addition.id, addition])),
    [foodItemAdditions],
  );

  const getAdditionAllergenChips = (addition) =>
    getAdditionAllergens(addition, additionsById, allergensById);

  // A pre-order validates against the chosen fulfillment time; before one is
  // chosen, an upcoming service-time window still counts (the group is listed by
  // getAvailableItemAdditionGroups under exactly that rule).
  const requestedAt = getEffectiveRequestedAt(checkout);
  const groupOrderCondition = useMemo(
    () =>
      getApplicableOrderCondition(
        additionGroup?.orderConditions,
        checkout?.orderType,
        requestedAt,
      ),
    [additionGroup?.orderConditions, checkout?.orderType, requestedAt],
  );

  // set initial values for minQty for additionGroupEntries
  useEffect(() => {
    if (additionGroup) {
      additionGroup.entries.forEach((entry) => {
        const entryOrderConditions = entry.orderConditions;
        const additionOrderConditions = entry.addition?.orderConditions;
        const mergedOrderCondition = mergeOrderConditions(
          [
            getApplicableOrderCondition(
              entryOrderConditions,
              checkout?.orderType,
              requestedAt,
            ),
            getApplicableOrderCondition(
              additionOrderConditions,
              checkout?.orderType,
              requestedAt,
            ),
          ].filter(Boolean),
        );

        if (mergedOrderCondition?.minQty > 0) {
          handleQtyChange(entry, mergedOrderCondition?.minQty); // set the minQty for each
        } else if (entry?.isPreSelected) {
          handleQtyChange(entry, 1); // set qty=1 for each that is pre-selected
        }
      });
    }
  }, [additionGroup]);

  // validate the Group on each selectedOrderItemAdditions change
  useEffect(() => {
    validateGroup();
  }, [selectedOrderItemAdditions]);

  const getSelectedOrderItemAdditionFromGroup = (additionGroupEntry) =>
    additionGroupEntry &&
    selectedOrderItemAdditions?.find(
      (orderItemAddition) =>
        orderItemAddition.additionGroupEntry.id === additionGroupEntry.id,
    );

  const getSelectedOrderItemAdditionsFromGroup = (excludeAdditionGroupEntry) =>
    additionGroup &&
    selectedOrderItemAdditions?.filter(
      ({ additionGroupEntry }) =>
        additionGroupEntry &&
        additionGroupEntry.group?.id === additionGroup.id &&
        (!excludeAdditionGroupEntry ||
          additionGroupEntry.id !== excludeAdditionGroupEntry.id),
    );

  // calculates qty of additionEntries within one group
  const getQtyInGroup = (excludeAdditionGroupEntry) =>
    getSelectedOrderItemAdditionsFromGroup(excludeAdditionGroupEntry).reduce(
      (sum, { qty }) => sum + qty,
      0,
    );

  // calculates the PAID total of additionEntries within one group (mirrors the
  // backend's paidTotal: free-of-charge selections are excluded from the total)
  const getTotalInGroup = (excludeAdditionGroupEntry) => {
    const freeOfChargeSelections = additionGroup?.freeOfChargeSelections || 0;
    let currentQty = 1;
    let total = 0;
    getSelectedOrderItemAdditionsFromGroup(excludeAdditionGroupEntry).forEach(
      ({ qty, additionGroupEntry }) => {
        const price = getItemDataOrAdditionPrice(
          checkout?.orderType,
          additionGroupEntry.addition,
        );
        for (let i = 0; i < qty; i++) {
          if (currentQty > freeOfChargeSelections) total += price;
          currentQty++;
        }
      },
    );
    return total;
  };

  const isQtySatisfied = (orderCondition, qty) => {
    if (!orderCondition) return true;
    const minOk =
      orderCondition.minQty === null ||
      orderCondition.minQty === undefined ||
      orderCondition.minQty <= qty;
    const maxOk =
      orderCondition.maxQty === null ||
      orderCondition.maxQty === undefined ||
      orderCondition.maxQty >= qty;
    return minOk && maxOk;
  };

  const isTotalSatisfied = (orderCondition, total) => {
    if (!orderCondition) return true;
    const minOk =
      orderCondition.minTotal === null ||
      orderCondition.minTotal === undefined ||
      orderCondition.minTotal <= total;
    const maxOk =
      orderCondition.maxTotal === null ||
      orderCondition.maxTotal === undefined ||
      orderCondition.maxTotal >= total;
    return minOk && maxOk;
  };

  const validateGroup = () => {
    if (groupOrderCondition) {
      const { minQty, maxQty, minTotal, maxTotal } = groupOrderCondition;
      const qty = getQtyInGroup();
      const total = getTotalInGroup();
      const hasMaxQty = maxQty !== null && maxQty !== undefined;
      const hasMinTotal = minTotal !== null && minTotal !== undefined;
      const hasMaxTotal = maxTotal !== null && maxTotal !== undefined;
      const formatTotal = (value) =>
        intl.formatNumber(value, { format: 'priceCurrency' });
      let error = null;
      if (hasMaxQty && minQty === maxQty && (qty < minQty || qty > maxQty)) {
        error = {
          error: 'MIN_MAX_QTY_NOT_SATISFIED',
          messageId: 'itemAdditionGroupExactQtyErrorMessage',
          value: minQty,
        };
      } else if (qty < minQty) {
        error = {
          error: 'MIN_QTY_NOT_SATISFIED',
          messageId: 'itemAdditionGroupMinQtyErrorMessage',
          value: minQty,
        };
      } else if (hasMaxQty && qty > maxQty) {
        error = {
          error: 'MAX_QTY_NOT_SATISFIED',
          messageId: 'itemAdditionGroupMaxQtyErrorMessage',
          value: maxQty,
        };
      } else if (
        hasMinTotal &&
        hasMaxTotal &&
        minTotal === maxTotal &&
        (total < minTotal || total > maxTotal)
      ) {
        error = {
          error: 'MIN_MAX_TOTAL_NOT_SATISFIED',
          messageId: 'itemAdditionGroupExactTotalErrorMessage',
          value: formatTotal(minTotal),
        };
      } else if (hasMinTotal && total < minTotal) {
        error = {
          error: 'MIN_TOTAL_NOT_SATISFIED',
          messageId: 'itemAdditionGroupMinTotalErrorMessage',
          value: formatTotal(minTotal),
        };
      } else if (hasMaxTotal && total > maxTotal) {
        error = {
          error: 'MAX_TOTAL_NOT_SATISFIED',
          messageId: 'itemAdditionGroupMaxTotalErrorMessage',
          value: formatTotal(maxTotal),
        };
      }

      setIsGroupSatisfied(!error);
      setError(error);
    } else {
      if (!additionGroup?.orderConditions?.length) {
        setIsGroupSatisfied(true);
      } else {
        setIsGroupSatisfied(false);
        console.debug(
          `No OrderConditions found for Group (id="${additionGroup.id}")`,
        );
      }
    }
  };

  const handleQtyChange = (additionGroupEntry, qty) => {
    setSelectedOrderItemAdditions((prevState) =>
      prevState?.some(
        (orderItemAddition) =>
          orderItemAddition.additionGroupEntry.id === additionGroupEntry.id,
      )
        ? prevState
            .map((orderItemAddition) =>
              orderItemAddition.additionGroupEntry.id === additionGroupEntry.id
                ? qty > 0 && { qty, additionGroupEntry }
                : orderItemAddition,
            )
            .filter(Boolean)
        : [...prevState, { qty, additionGroupEntry }],
    );
  };

  return (
    <>
      {!!additionGroup?.entries?.length && (
        <div>
          <div
            className={classnames('d-flex align-items-center', {
              'mt-1': index > 0,
            })}
          >
            <span className="font-medium-1 text-primary">
              {getLocalizedName(additionGroup, intl.locale)}
            </span>
            {!!additionGroup?.freeOfChargeSelections && (
              <Alert
                className="ml-50 d-inline-flex px-50 py-25 mb-0"
                color="success"
              >
                <span className="font-small-3">
                  <FormattedMessage
                    id="freeOfChargeSelectionsText"
                    values={{ qty: additionGroup.freeOfChargeSelections }}
                  />
                </span>
              </Alert>
            )}
          </div>

          {error && (
            <Alert
              className="d-inline-flex px-50 py-25 mt-50 mb-0"
              color="danger"
            >
              <span className="font-small-3">
                <FormattedMessage
                  id={error.messageId}
                  values={{ value: error.value }}
                />
              </span>
            </Alert>
          )}

          {additionGroup.entries
            .filter(
              (additionGroupEntry) =>
                !!additionGroupEntry?.addition &&
                additionGroupEntry.addition.isActive !== false,
            )
            .map((additionGroupEntry, index) => {
            // add the AdditionGroup to each AdditionGroupEntry
            additionGroupEntry.group = {
              ...additionGroup,
              entries: undefined, // exclude entries to avoid circular structure
            };

            const itemAddition = additionGroupEntry.addition;
            const additionAllergens = getAdditionAllergenChips(itemAddition);
            const selectedOrderItemAddition = getSelectedOrderItemAdditionFromGroup(
              additionGroupEntry,
            );
            const qty = selectedOrderItemAddition?.qty || 0;
            // merge OrderConditions of the Entry and the ItemAddition to be able to set the constraints below
            const mergedOrderCondition = mergeOrderConditions(
              [
                getApplicableOrderCondition(
                  additionGroupEntry?.orderConditions,
                  checkout?.orderType,
                  requestedAt,
                ),

                getApplicableOrderCondition(
                  itemAddition?.orderConditions,
                  checkout?.orderType,
                  requestedAt,
                ),
              ].filter(Boolean),
            );
            const handleQtySelectorOnClick = (newQty, messageId) => {
              const _isQtySatisfied = isQtySatisfied(
                mergedOrderCondition,
                newQty,
              );
              const _isTotalSatisfied = isTotalSatisfied(
                mergedOrderCondition,
                newQty *
                  getItemDataOrAdditionPrice(checkout?.orderType, itemAddition),
              );

              if (_isQtySatisfied && _isTotalSatisfied) {
                handleQtyChange(additionGroupEntry, newQty); // handle the qty change if everything is valid
              } else {
                const msg = intl.formatMessage(
                  { id: messageId },
                  { qty: mergedOrderCondition?.maxQty },
                );
                customMessageNotification('warning', msg);
              }
            };

            return (
              <>
                <div
                  key={index}
                  style={{ transition: 'border 300ms', height: '44px' }}
                  className={classnames(
                    'd-flex align-items-center my-50 px-75 py-50 rounded',
                    { border: !selectedOrderItemAddition },
                    { 'border-primary': !!selectedOrderItemAddition },
                  )}
                >
                  <div className="d-flex align-items-center flex-grow-0">
                    {groupOrderCondition?.maxQty === 1 ? (
                      qty > 0 ? (
                        <Icon.CheckSquare
                          className="vx-icon mr-50 primary"
                          size={24}
                          style={{ minHeight: '24px' }}
                          onClick={() =>
                            handleQtySelectorOnClick(
                              0,
                              'additionMaxQtyExceededNotification',
                            )
                          }
                        />
                      ) : (
                        <Icon.Square
                          className="vx-icon mr-50"
                          size={24}
                          style={{ minWidth: '24px' }}
                          onClick={() =>
                            handleQtySelectorOnClick(
                              1,
                              'additionMaxQtyExceededNotification',
                            )
                          }
                        />
                      )
                    ) : (
                      <QtySelector
                        value={qty}
                        label={getLocalizedName(itemAddition, intl.locale)}
                        onClickPlus={() =>
                          handleQtySelectorOnClick(
                            qty + 1,
                            'additionMaxQtyExceededNotification',
                          )
                        }
                        onClickMinus={() =>
                          handleQtySelectorOnClick(
                            qty - 1,
                            'additionMaxQtyExceededNotification',
                          )
                        }
                      />
                    )}
                  </div>

                  <div className="flex-grow-1 px-75 font-medium-1">
                    {/* The name keeps its own truncation so the tag chips below
                        it are not clipped by `truncate`'s nowrap/overflow on the
                        container. */}
                    <div className="truncate">
                      <Marquee>
                        {getLocalizedName(itemAddition, intl.locale)}
                      </Marquee>
                    </div>
                    <AllergenChips allergens={additionAllergens} compact />
                  </div>

                  <div className="flex-grow-0 font-medium-1 text-bold-500">
                    {!getItemDataOrAdditionPrice(
                      checkout?.orderType,
                      itemAddition,
                    ) ||
                    additionGroup?.freeOfChargeSelections > getQtyInGroup() ? (
                      intl.formatMessage({ id: 'priceFree' }).toUpperCase()
                    ) : (
                      <FormattedNumber
                        value={getItemDataOrAdditionPrice(
                          checkout?.orderType,
                          itemAddition,
                        )}
                        format="priceCurrency"
                      />
                    )}
                  </div>
                </div>
              </>
            );
          })}
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  cart: state.store.cart,
  checkout: state.store.checkout,
  foodAllergens: state.store.foodAllergens,
  foodItemAdditions: state.store.foodItemAdditions,
});

export default connect(mapStateToProps, {
  setIsSelectAdditionsModalOpen,
})(AdditionGroup);
